/**
 * Query/mutation hooks for the users-admin screens, plus the one derived fact
 * every screen needs: which countries the CALLER may administer (D5).
 *
 * Invalidation is deliberately coarse — the `['users']` prefix — because the
 * same data renders in three places (admin list, admin detail, the pipeline's
 * colleague picker) and a surgical miss here is a stale picker there. See the
 * key-shape note in `users-api.ts`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from '../../auth/session';
import { COUNTRY_ORDER } from '../../domain/countries';
import type { CountryCode } from '../../domain/countries';
import {
  adminUserKeys,
  createUser,
  fetchUser,
  fetchUserOverrides,
  fetchUserRoles,
  listUsers,
  putUserOverrides,
  putUserRoles,
  updateUser,
} from './users-api';
import type {
  AdminUserDto,
  CreateUserBody,
  ListUsersQuery,
  PermissionOverride,
  RoleAssignment,
  UpdateUserBody,
} from './users-api';

/**
 * The countries this caller may administer users IN: where they hold
 * `users.manage` (every country for `grp:true`). This is the client half of
 * `authorityCountries()` in `d5-boundary.ts:147` — the set the server will
 * accept role/override entries for, and therefore the set the editors offer.
 */
export function useManageableCountries(): readonly CountryCode[] {
  const { isGroupAdmin, permsIn } = useSession();
  if (isGroupAdmin) return COUNTRY_ORDER;
  return COUNTRY_ORDER.filter((code) => permsIn(code).includes('users.manage'));
}

/* ─────────────────────────────────────────────────────────────────── reads ── */

export function useAdminUsersList(query: ListUsersQuery, enabled = true) {
  return useQuery({
    queryKey: adminUserKeys.list(query),
    queryFn: ({ signal }) => listUsers(query, signal),
    // `false` for a caller with no users.manage anywhere: the page renders its
    // refusal sentence instead, and firing a guaranteed-403 request behind it
    // would be a request spent proving what the client already knows.
    enabled,
    // Admin data changes when an admin changes it; while typing a search the
    // previous page staying on screen beats a skeleton flash per keystroke.
    placeholderData: (previous) => previous,
  });
}

export function useAdminUser(id: string) {
  return useQuery({
    queryKey: adminUserKeys.detail(id),
    queryFn: ({ signal }) => fetchUser(id, signal),
  });
}

export function useUserRoles(id: string) {
  return useQuery({
    queryKey: adminUserKeys.roles(id),
    queryFn: ({ signal }) => fetchUserRoles(id, signal),
  });
}

export function useUserOverrides(id: string) {
  return useQuery({
    queryKey: adminUserKeys.overrides(id),
    queryFn: ({ signal }) => fetchUserOverrides(id, signal),
  });
}

/* ─────────────────────────────────────────────────────────────── mutations ── */

function useInvalidateUsers(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: adminUserKeys.root() });
}

export function useCreateUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (body: CreateUserBody) => createUser(body),
    onSuccess: invalidate,
  });
}

/** PATCH — grp-only on the server (D5b); the UI renders its callers grp-only. */
export function useUpdateUser(id: string) {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (body: UpdateUserBody) => updateUser(id, body),
    onSuccess: invalidate,
  });
}

export function usePutUserRoles(id: string) {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (assignments: RoleAssignment[]) => putUserRoles(id, assignments),
    onSuccess: invalidate,
  });
}

export function usePutUserOverrides(id: string) {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (overrides: PermissionOverride[]) => putUserOverrides(id, overrides),
    onSuccess: invalidate,
  });
}

export type { AdminUserDto };
